import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Building2,
  CalendarDays,
  Mail,
  MapPin,
  Phone,
  User as UserIcon,
  Users,
} from "lucide-react";
import { Badge } from "../ui/Badge";
import { Card } from "../ui/Card";
import { EmptyState } from "../ui/EmptyState";
import { SectionCard } from "../ui/SectionCard";
import { Avatar } from "../ui/Avatar";
import { cn } from "../../utils/cn";

// ─────────────────────────────────────────────────────────────────────────────
// CompanyInfoCard — bloque reutilizable de presentación de los datos de la
// empresa. Read-only. Se usa en:
//   · /home/configuracion-empresa (comercio: vista propia de su empresa)
//   · cualquier vista admin que quiera mostrar la ficha rápida de una empresa
//
// Composición: hero de identidad (logo o iniciales + nombre + id fiscal +
// razón social + dirección fiscal) y una grilla responsive con Operación /
// Direcciones de envío (columna principal) y un rail con el contenido `aside`
// que pase el contenedor más los Contactos.
//
// Para EDITAR los campos, admin usa /admin/settings → Organización.
// ─────────────────────────────────────────────────────────────────────────────

// Orden canónico (lunes primero) y formato que escribe el admin hoy.
export const WEEK_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Hay empresas con `workingDays` guardados en otros formatos (p. ej. las
// iniciales "L, M, X, J, V" de cargas antiguas). Sin normalizar, ningún día
// matcheaba y la ficha mostraba la semana entera inactiva más chips sueltos.
// Convención de iniciales en español: X = miércoles, para no chocar con M.
const DAY_ALIASES = {
  monday: 0, mon: 0, lunes: 0, lun: 0, lu: 0, l: 0,
  tuesday: 1, tue: 1, tues: 1, martes: 1, mar: 1, ma: 1, m: 1,
  wednesday: 2, wed: 2, miercoles: 2, mie: 2, mi: 2, x: 2,
  thursday: 3, thu: 3, thur: 3, thurs: 3, jueves: 3, jue: 3, ju: 3, j: 3,
  friday: 4, fri: 4, viernes: 4, vie: 4, vi: 4, v: 4,
  saturday: 5, sat: 5, sabado: 5, sab: 5, sa: 5, s: 5,
  sunday: 6, sun: 6, domingo: 6, dom: 6, do: 6, d: 6,
};

function stripAccents(text) {
  return String(text ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

/** Día en cualquier formato conocido → nombre canónico en inglés, o null. */
export function normalizeWorkingDay(value) {
  if (typeof value !== "string") return null;
  const key = stripAccents(value).replace(/\.$/, "");
  const idx = DAY_ALIASES[key];
  return idx === undefined ? null : WEEK_DAYS[idx];
}

// Etiquetas localizadas con Intl en vez de un mapa hardcodeado: el lunes
// 2024-01-01 (UTC) ancla el índice 0, así siguen el idioma activo.
function weekdayLabels(locale, weekday) {
  try {
    const fmt = new Intl.DateTimeFormat(locale, { weekday, timeZone: "UTC" });
    return WEEK_DAYS.map((_, i) => fmt.format(new Date(Date.UTC(2024, 0, 1 + i))));
  } catch {
    // Locale inválido: caemos al inglés antes que romper la vista.
    return WEEK_DAYS.map((d) => (weekday === "short" ? d.slice(0, 3) : d));
  }
}

function listFormat(locale, items) {
  try {
    return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(items);
  } catch {
    return items.join(", ");
  }
}

function capitalize(text) {
  return text ? text.charAt(0).toLocaleUpperCase() + text.slice(1) : text;
}

function initialsOf(name) {
  if (!name || typeof name !== "string") return "";
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function sameText(a, b) {
  return (
    typeof a === "string" &&
    typeof b === "string" &&
    a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase()
  );
}

function hasContactData(c) {
  return Boolean(c && (c.name || c.email || c.phone));
}

// En `companies.contacts` conviven dos formas: `primary` / `billing` (objetos,
// lo que escribe "Editar ficha") y las listas `operationsContacts` /
// `billingContacts` (las escribe el alta de contrato; en datos históricos son
// strings con el correo). Antes solo se leía la primera y las empresas dadas
// de alta por el wizard no mostraban contactos. Por grupo gana el objeto, que
// es el que se edita; la lista queda de respaldo para no mostrar datos viejos
// junto a los nuevos.
function collectContacts(contacts, t) {
  if (!contacts || typeof contacts !== "object") return [];
  const fromList = (list) =>
    (Array.isArray(list) ? list : [])
      .map((c) => (typeof c === "string" ? { email: c.trim() } : c))
      .filter(hasContactData);
  const group = (single, list, label) =>
    hasContactData(single)
      ? [{ group: label, contact: single }]
      : fromList(list).map((contact) => ({ group: label, contact }));
  return [
    ...group(contacts.primary, contacts.operationsContacts, t("Operaciones")),
    ...group(contacts.billing, contacts.billingContacts, t("Facturación")),
  ];
}

// La dirección guardada suele traer ya comuna y región ("…, La Reina, Región
// Metropolitana, Chile"); repetirlas abajo es ruido. Solo se muestran si
// aportan algo que la línea principal no dice.
function addressLocality(a) {
  const address = String(a.address || "").toLocaleLowerCase();
  const parts = [a.municipality, a.state].filter(
    (p) => p && !address.includes(String(p).toLocaleLowerCase()),
  );
  return parts.join(", ");
}

// Padding real de SectionCard: su `p-5` lo pisa el `.p-5` de Bootstrap
// (3rem !important). La clase arbitraria no colisiona (ver ui/Card.jsx).
const SECTION_PAD = "p-[1.25rem]";

const MICRO_LABEL = "text-[11px] font-semibold text-fg-muted uppercase tracking-wider";
const LINK_FOCUS =
  "rounded hover:text-fg hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";

export default function CompanyInfoCard({ company, dense = false, aside = null }) {
  const { t, i18n } = useTranslation();

  // URL de logo que falló al cargar (404, CORS): volvemos a las iniciales en
  // vez de mostrar una imagen rota.
  const [failedLogo, setFailedLogo] = useState(null);
  const shortDays = useMemo(() => weekdayLabels(i18n.language, "short"), [i18n.language]);
  const longDays = useMemo(() => weekdayLabels(i18n.language, "long"), [i18n.language]);

  if (!company) {
    return (
      <div className="rounded-lg border border-border bg-bg-muted">
        <EmptyState
          icon={<Building2 />}
          title={t("Sin empresa asociada")}
          description={t("Tu usuario aún no está vinculado a una empresa.")}
        />
      </div>
    );
  }

  const rawDays = Array.isArray(company.workingDays) ? company.workingDays : [];
  const activeIdx = new Set();
  // Valores fuera de todo formato conocido no se pierden: chips extra.
  const unknownDays = [];
  rawDays.forEach((d) => {
    const canonical = normalizeWorkingDay(d);
    if (canonical) activeIdx.add(WEEK_DAYS.indexOf(canonical));
    else if (d != null && String(d).trim()) unknownDays.push(String(d));
  });
  const sortedIdx = [...activeIdx].sort((a, b) => a - b);
  const isRange =
    sortedIdx.length >= 3 &&
    sortedIdx[sortedIdx.length - 1] - sortedIdx[0] === sortedIdx.length - 1;
  let daysSummary = "";
  if (sortedIdx.length === 7) daysSummary = t("Todos los días");
  else if (isRange) {
    daysSummary = capitalize(
      t("{{from}} a {{to}}", {
        from: longDays[sortedIdx[0]],
        to: longDays[sortedIdx[sortedIdx.length - 1]],
      }),
    );
  } else if (sortedIdx.length) {
    daysSummary = capitalize(listFormat(i18n.language, sortedIdx.map((i) => longDays[i])));
  }

  const legalName = sameText(company.legalName, company.name) ? null : company.legalName;
  const addresses = Array.isArray(company.shippingAddresses)
    ? company.shippingAddresses.filter(Boolean)
    : [];
  const contacts = collectContacts(company.contacts, t);

  const hasOperacion = rawDays.length > 0 || Boolean(company.pickUpTime);
  const hasAddresses = addresses.length > 0;
  const hasMain = hasOperacion || hasAddresses;
  // Contactos se muestra si la empresa trae el objeto aunque esté vacío:
  // "sin contactos" es información útil, no una sección que desaparece.
  const showContacts = contacts.length > 0 || Boolean(company.contacts);
  const hasRail = Boolean(aside) || showContacts;

  return (
    <div className={cn("space-y-4", dense && "space-y-3")}>
      <Card className="flex items-center gap-[1rem]">
        {company.logo && company.logo !== failedLogo ? (
          <div className="w-16 h-16 rounded-lg border border-border bg-white flex items-center justify-center p-1.5 shrink-0 overflow-hidden">
            <img
              src={company.logo}
              alt=""
              onError={() => setFailedLogo(company.logo)}
              className="as-company-logo max-h-full max-w-full object-contain"
            />
          </div>
        ) : (
          <div
            className="w-16 h-16 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center text-xl font-semibold uppercase shrink-0"
            aria-hidden
          >
            {initialsOf(company.name) || <Building2 className="w-6 h-6" />}
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-x-2.5 gap-y-1 flex-wrap">
            <h2 className="text-xl font-semibold text-fg leading-tight min-w-0 break-words">
              {company.name || "—"}
            </h2>
            {company.businessIdentifier && (
              <Badge tone="neutral" className="font-mono tabular-nums shrink-0">
                {company.businessIdentifier}
              </Badge>
            )}
          </div>
          {legalName && <p className="text-sm text-fg-muted">{legalName}</p>}
          {company.fiscalAddress && (
            <p className="text-sm text-fg-muted flex items-start gap-1.5 min-w-0">
              <MapPin className="w-3.5 h-3.5 shrink-0 mt-[3px]" aria-hidden />
              <span className="min-w-0 break-words">{company.fiscalAddress}</span>
            </p>
          )}
        </div>
      </Card>

      {(hasMain || hasRail) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-[1rem] items-start">
          {hasMain && (
            // Sin rail, la columna principal ocupa todo el ancho en vez de
            // dejar un tercio vacío.
            <div className={cn("space-y-4 min-w-0", hasRail ? "lg:col-span-2" : "lg:col-span-3")}>
              {hasOperacion && (
                <SectionCard
                  tone="bg"
                  className={SECTION_PAD}
                  icon={<CalendarDays className="w-4 h-4" aria-hidden />}
                  title={t("Operación")}
                >
                  <dl className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-x-8 gap-y-4">
                    <div className="space-y-2 min-w-0">
                      <dt className={MICRO_LABEL}>{t("Días laborales")}</dt>
                      <dd className="space-y-2">
                        <p className={cn("text-sm", daysSummary ? "font-medium text-fg" : "text-fg-muted")}>
                          {daysSummary ||
                            (unknownDays.length ? unknownDays.join(", ") : t("Sin definir"))}
                        </p>
                        {/* Los chips son la representación visual; el resumen
                            de arriba ya lo dice en texto. Activo vs. inactivo
                            se distingue también por la forma (borde sólido vs.
                            punteado), no solo por color. */}
                        {(sortedIdx.length > 0 || unknownDays.length > 0) && (
                        <div
                          className="grid grid-cols-7 gap-1 sm:flex sm:flex-wrap sm:gap-1.5"
                          aria-hidden
                        >
                          {WEEK_DAYS.map((day, i) => (
                            <span
                              key={day}
                              className={cn(
                                "inline-flex items-center justify-center min-w-0 sm:min-w-[2.75rem] h-7 px-[0.25rem] sm:px-[0.5rem] rounded-md border text-xs capitalize",
                                activeIdx.has(i)
                                  ? "bg-brand-50 text-brand-700 border-brand-200 font-semibold"
                                  : "border-dashed border-border text-fg-subtle",
                              )}
                            >
                              {shortDays[i]}
                            </span>
                          ))}
                          {unknownDays.map((day, i) => (
                            <span
                              key={`${day}-${i}`}
                              className="inline-flex items-center h-7 px-2 rounded-md border text-xs font-semibold bg-brand-50 text-brand-700 border-brand-200"
                            >
                              {day}
                            </span>
                          ))}
                        </div>
                        )}
                      </dd>
                    </div>
                    <div className="space-y-2">
                      <dt className={MICRO_LABEL}>{t("Horario de retiro")}</dt>
                      <dd
                        className={cn(
                          "text-sm",
                          company.pickUpTime ? "font-medium text-fg tabular-nums" : "text-fg-muted",
                        )}
                      >
                        {company.pickUpTime || t("Sin definir")}
                      </dd>
                    </div>
                  </dl>
                </SectionCard>
              )}

              {hasAddresses && (
                <SectionCard
                  tone="bg"
                  className={SECTION_PAD}
                  icon={<MapPin className="w-4 h-4" aria-hidden />}
                  title={
                    <span className="inline-flex items-center gap-2">
                      {t("Direcciones de envío")}
                      <Badge tone="neutral" className="tabular-nums">
                        {addresses.length}
                      </Badge>
                    </span>
                  }
                >
                  <ul className="divide-y divide-border -my-1">
                    {addresses.map((a, idx) => {
                      const locality = addressLocality(a);
                      return (
                        <li key={a._id || idx} className="py-[0.75rem] first:pt-[0.25rem] last:pb-[0.25rem]">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm font-medium text-fg min-w-0 break-words">
                              {a.address || "—"}
                            </p>
                            {a.type && (
                              <Badge tone="neutral" className="shrink-0">
                                {a.type}
                              </Badge>
                            )}
                          </div>
                          {(locality || a.contactPhone) && (
                            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fg-muted">
                              {locality && <span>{locality}</span>}
                              {a.contactPhone && (
                                <a
                                  href={`tel:${a.contactPhone}`}
                                  className={cn("inline-flex items-center gap-1.5 tabular-nums", LINK_FOCUS)}
                                >
                                  <Phone className="w-3 h-3 shrink-0" aria-hidden />
                                  {a.contactPhone}
                                </a>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </SectionCard>
              )}
            </div>
          )}

          {hasRail && (
            <div
              className={cn(
                "space-y-4 min-w-0",
                // Sin columna principal, el rail se reparte a lo ancho.
                !hasMain &&
                  "lg:col-span-3 md:grid md:grid-cols-2 md:gap-[1rem] md:space-y-0 md:items-start",
              )}
            >
              {aside}
              {showContacts && (
                <SectionCard
                  tone="bg"
                  className={SECTION_PAD}
                  icon={<Users className="w-4 h-4" aria-hidden />}
                  title={t("Contactos")}
                >
                  {contacts.length > 0 ? (
                    <ul className="divide-y divide-border -my-1">
                      {contacts.map(({ group, contact }, i) => (
                        <ContactRow key={`${group}-${i}`} group={group} contact={contact} />
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-fg-muted">{t("Sin contactos registrados")}</p>
                  )}
                </SectionCard>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ContactRow({ group, contact }) {
  // El alta guarda roles como "OPERACIONES" / "FACTURACION Y COBRANZA", que
  // repiten la etiqueta del grupo: solo se muestran si dicen algo más.
  const showRole =
    contact.role && !stripAccents(contact.role).startsWith(stripAccents(group));
  return (
    <li className="py-[0.75rem] first:pt-[0.25rem] last:pb-[0.25rem] space-y-2 min-w-0">
      <div className={MICRO_LABEL}>{group}</div>
      <div className="flex items-center gap-2.5 min-w-0">
        <Avatar
          size="sm"
          tone="brand"
          initials={initialsOf(contact.name) || <UserIcon className="w-3.5 h-3.5" aria-hidden />}
        />
        <div className="min-w-0">
          {contact.name && <div className="text-sm font-medium text-fg truncate">{contact.name}</div>}
          {showRole && <div className="text-xs text-fg-muted truncate">{contact.role}</div>}
        </div>
      </div>
      {(contact.email || contact.phone) && (
        <div className="space-y-1 pl-[2.375rem]">
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className={cn("flex items-center gap-1.5 min-w-0 text-xs text-fg-muted", LINK_FOCUS)}
            >
              <Mail className="w-3 h-3 shrink-0" aria-hidden />
              <span className="truncate">{contact.email}</span>
            </a>
          )}
          {contact.phone && (
            <a
              href={`tel:${contact.phone}`}
              className={cn("flex items-center gap-1.5 text-xs text-fg-muted tabular-nums", LINK_FOCUS)}
            >
              <Phone className="w-3 h-3 shrink-0" aria-hidden />
              {contact.phone}
            </a>
          )}
        </div>
      )}
    </li>
  );
}
